import { translate } from "./translations";

export const MAX_IMAGE_DIMENSION_PX = 4000;
export const MAX_IMAGE_SIZE_MB = 10;

export function hasValidFileSize(file: File, maxMegabytes: number): boolean {
    return file.size <= maxMegabytes * 1024 * 1024;
}

export function isValidImageFormat(file: File): boolean {
    return ["image/jpeg", "image/png", "image/webp"].includes(file.type);
}

export type ImageValidationResult = {
    valid: boolean;
    error?: string;
    width?: number;
    height?: number;
};

/**
 * Validates image dimensions by loading it in the browser.
 * This prevents uploading images that are too large to process on the server.
 *
 * @param {File} file - The image file to validate
 * @param {number} maxDimension - Maximum allowed width or height in pixels
 * @returns {Promise<{valid: boolean, error?: string, width?: number, height?: number}>}
 */
export function validateImageDimensions(file: File, maxDimension: number): Promise<ImageValidationResult> {
    return new Promise(resolve => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            const width = img.naturalWidth;
            const height = img.naturalHeight;

            if (width > maxDimension || height > maxDimension) {
                resolve({
                    valid: false,
                    error: translate("common.image_dimensions_too_large", false, [
                        width.toString(),
                        height.toString(),
                        maxDimension.toString(),
                    ]),
                    width,
                    height,
                });
            } else {
                resolve({ valid: true, width, height });
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({
                valid: false,
                error: translate("page.admin_edit_event.invalid_image_type"),
            });
        };

        img.src = url;
    });
}
